import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { getClusterChipStyle } from '../../../utils/clusterUtils';
import OrgStructureTypeSelector, { NO_TYPE_VALUE } from '../../common/OrgStructureTypeSelector';
import ClusterDeleteDialog from './components/ClusterDeleteDialog';

const ClustersConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [clusters, setClusters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // ADR-119: multi-select filter on Organizational Structure Type (ids, or NO_TYPE_VALUE)
  const [structureTypeFilter, setStructureTypeFilter] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, cluster: null });
  const [isLoading, setIsLoading] = useState(true);

  /*
  const renderDepartmentHierarchy = (departments, level = 0) => {
    return departments.map(department => (
      <React.Fragment key={department.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '4px',
            pl: level * 2,
            position: 'relative',
            '&::before': level > 0 ? {
              content: '""',
              position: 'absolute',
              left: (level * 16) - 12,
              top: '10px',
              width: '12px',
              height: '1px',
              backgroundColor: '#6c757d',
            } : {},
            '&::after': level > 0 ? {
              content: '""',
              position: 'absolute',
              left: (level * 16) - 12,
              top: 0,
              width: '1px',
              height: '10px',
              backgroundColor: '#6c757d',
            } : {},
          }}
        >
          <Typography 
            variant="body2" 
            component="div"
            sx={{ 
              color: '#6c757d',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '2px',
              paddingLeft: '8px',
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
              {department.name}
              {department.departmentType && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '11px',
                    color: '#6B7280',
                    backgroundColor: '#F3F4F6',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    marginLeft: '4px'
                  }}
                >
                  {department.departmentType.name}
                </Typography>
              )}
              {department.manager && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <EmployeeAvatar employee={department.manager} size={16} />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '11px',
                      color: '#6B7280'
                    }}
                  >
                    {department.manager.fullName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Typography>
        </Box>
        {department.childDepts && department.childDepts.length > 0 && (
          <Box sx={{ ml: 2 }}>
            {renderDepartmentHierarchy(department.childDepts, level + 1)}
          </Box>
        )}
      </React.Fragment>
    ));
  };
  */

  const renderBusinessUnitHierarchy = (businessUnits, level = 0) => {
    return businessUnits.map(businessUnit => (
      <React.Fragment key={businessUnit.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '4px',
            pl: level * 2,
            position: 'relative',
            '&::before': level > 0 ? {
              content: '""',
              position: 'absolute',
              left: (level * 16) - 12,
              top: '10px',
              width: '12px',
              height: '1px',
              backgroundColor: '#6c757d',
            } : {},
            '&::after': level > 0 ? {
              content: '""',
              position: 'absolute',
              left: (level * 16) - 12,
              top: 0,
              width: '1px',
              height: '10px',
              backgroundColor: '#6c757d',
            } : {},
          }}
        >
          <Typography 
            variant="body2" 
            component="div"
            sx={{ 
              color: '#6c757d',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '2px',
              paddingLeft: '8px',
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
              {businessUnit.name}
              {businessUnit.owner && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <EmployeeAvatar employee={businessUnit.owner} size={16} />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '11px',
                      color: '#6B7280'
                    }}
                  >
                    {businessUnit.owner.fullName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Typography>
        </Box>
        {businessUnit.childBus && businessUnit.childBus.length > 0 && (
          <Box sx={{ ml: 2 }}>
            {renderBusinessUnitHierarchy(businessUnit.childBus, level + 1)}
          </Box>
        )}
      </React.Fragment>
    ));
  };

  const fetchClusters = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getClusters();
      // Fetch departments and business units for each cluster
      const clustersWithData = await Promise.all(data.map(async (cluster) => {
        try {
          const [/*departmentHierarchy,*/ businessUnitHierarchy] = await Promise.all([
            //organizationApi.getDepartmentHierarchy(cluster.id),
            organizationApi.getBusinessUnitHierarchy(cluster.id)
          ]);
          return {
            ...cluster,
            //departments: departmentHierarchy[0]?.childDepts || [],
            businessUnits: businessUnitHierarchy[0]?.childBus || []
          };
        } catch (err) {
          console.error(`Error fetching data for cluster ${cluster.id}:`, err);

          return {
            ...cluster,
            //departments: [],
            businessUnits: []
          };
        }
      }));
      setClusters(clustersWithData);
    } catch (err) {
      showAlert('Failed to load clusters.', 'error', err);
      console.error('Error fetching clusters:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClusters();
  }, []);

  const handleAddCluster = () => {
    navigate('/organization/clusters/new');
  };

  const handleEditCluster = (clusterId) => {
    navigate(`/organization/clusters/${clusterId}`);
  };

  const handleDeleteCluster = (cluster) => {
    setDeleteModal({ open: true, cluster });
  };

  // ADR-124: the dialog owns the preview and the delete call; this only
  // refreshes the list once the server has confirmed the deletion.
  const handleClusterDeleted = async (clusterName) => {
    setDeleteModal({ open: false, cluster: null });
    try {
      setIsLoading(true);
      await fetchClusters();
      showAlert(`${clusterName} deleted`, 'success');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredClusters = clusters.filter(cluster => {
    // ADR-119: type filter — any selected type, or "No type" for unclassified clusters
    if (structureTypeFilter.length > 0) {
      const typeId = cluster.orgStructureType?.id || NO_TYPE_VALUE;
      if (!structureTypeFilter.includes(typeId)) return false;
    }
    if (!searchTerm) return true;
    const needle = (searchTerm || '').toLowerCase();
    // ADR-119: search also matches the Organizational Structure Type name / code
    return cluster.name.toLowerCase().includes(needle)
      || (cluster.orgStructureType?.name || '').toLowerCase().includes(needle)
      || (cluster.orgStructureType?.code || '').toLowerCase().includes(needle);
  }).sort((a, b) => {
    // First sort by root status (root clusters come first)
    if (!a.parentCluster && b.parentCluster) return -1;
    if (a.parentCluster && !b.parentCluster) return 1;
    
    // Then sort alphabetically within each group
    return a.name.localeCompare(b.name);
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Clusters Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search Cluster"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSearch style={{ color: '#6B7280' }} />
                      </InputAdornment>
                    ),
                  }
                }}
                sx={{
                  minWidth: '300px',
                }}
              />
              <OrgStructureTypeSelector
                multiple
                showNoTypeOption
                label="Organizational Structure Type"
                value={structureTypeFilter}
                onChange={setStructureTypeFilter}
                size="small"
                fullWidth={false}
                sx={{ minWidth: 260 }}
              />
            </Box>
            <HeaderButton onClick={handleAddCluster}>
              <FaPlus /> Add Cluster
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredClusters.map((cluster) => (
                  <Grid item key={cluster.id}>
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '140px',
                        height: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: !cluster.parentCluster ? '#EFF6FF' : '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: !cluster.parentCluster ? '#E0F2FE' : '#f1f3f5'
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              color: '#212529',
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {cluster.name}
                          </Typography>
                          {/* ADR-119: Organizational Structure Type, right-aligned on the name row */}
                          {cluster.orgStructureType && (
                            <Chip
                              size="small"
                              label={cluster.orgStructureType.name}
                              title={`Organizational Structure Type: ${cluster.orgStructureType.name} (${cluster.orgStructureType.code})`}
                              sx={{ ...getClusterChipStyle(), flexShrink: 0 }}
                            />
                          )}
                        </Box>
                        
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#6c757d',
                            fontSize: '12px',
                            fontStyle: 'italic',
                            marginBottom: '12px'
                          }}
                        >
                          {cluster.parentCluster ? `part of ${cluster.parentCluster.name}` : 'Global Cluster'}
                        </Typography>

                        <Box sx={{ marginBottom: '16px' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6c757d',
                              fontSize: '13px',
                              fontWeight: 500,
                              marginBottom: '4px'
                            }}
                          >
                            Representative:
                          </Typography>
                          {cluster.manager ? (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              paddingLeft: '8px'
                            }}>
                              <EmployeeAvatar employee={cluster.manager} size={24} />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#6c757d',
                                  fontSize: '12px'
                                }}
                              >
                                {cluster.manager.fullName}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#adb5bd',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                paddingLeft: '8px'
                              }}
                            >
                              No manager assigned
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ marginBottom: '40px' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6c757d',
                              fontSize: '13px',
                              fontWeight: 500,
                              marginBottom: '4px'
                            }}
                          >
                            Companies:
                          </Typography>
                          {cluster.companies?.length > 0 ? (
                            cluster.companies.map(company => (
                              <Typography 
                                key={company.id}
                                variant="body2" 
                                component="div"
                                sx={{ 
                                  color: '#6c757d',
                                  fontSize: '12px',
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px',
                                  marginBottom: '2px',
                                  paddingLeft: '8px'
                                }}
                              >
                                <span style={{ 
                                  width: '4px', 
                                  height: '4px', 
                                  backgroundColor: '#6c757d',
                                  borderRadius: '50%',
                                  display: 'inline-block'
                                }} />
                                {company.name} ({company.country?.name})
                              </Typography>
                            ))
                          ) : (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#adb5bd',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                paddingLeft: '8px'
                              }}
                            >
                              No companies in this cluster
                            </Typography>
                          )}

                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6c757d',
                              fontSize: '13px',
                              fontWeight: 500,
                              marginTop: '12px',
                              marginBottom: '4px'
                            }}
                          >
                            Business Units:
                          </Typography>
                          {cluster.businessUnits?.length > 0 ? (
                            <Box sx={{ pl: 1 }}>
                              {renderBusinessUnitHierarchy(cluster.businessUnits)}
                            </Box>
                          ) : (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#adb5bd',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                paddingLeft: '8px'
                              }}
                            >
                              No business units in this cluster
                            </Typography>
                          )}

                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          <Button
                            onClick={() => handleEditCluster(cluster.id)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#0d6efd',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(13, 110, 253, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                color: '#0d6efd'
                              }
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDeleteCluster(cluster)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                color: '#dc3545'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      <ClusterDeleteDialog
        isOpen={deleteModal.open}
        cluster={deleteModal.cluster}
        onClose={() => setDeleteModal({ open: false, cluster: null })}
        onDeleted={handleClusterDeleted}
      />
    </Container>
  );
};

export default ClustersConfiguration; 